import { useThrottledCallback } from "@react-hookz/web";
import { useCallback, useEffect, useMemo } from "react";
import { type TQuickAccess, useQuickAccess } from "./useQuickAccess";
import { type TGlobalKeyboardShortcut, ctrl, globalKeyboardShortcuts, keysMatch, modifierKeyPressed } from "./utils";
import { useConfigureAlertsModal } from "../modals/Alerts";
import { getProcessedSelectedCellsByRange } from "../../../tableUtils";
import { store } from "../../modal";
import { focusedPaneIdAtom, gridApiAtomFamily } from "../stores";

export function useShortcuts(debounce?: number) {
  const { data: quickAccess, handleQuickAccess } = useQuickAccess();
  const alertsModal = useConfigureAlertsModal();

  const contextShortcuts = useMemo(
    (): TGlobalKeyboardShortcut[] => [
      {
        name: "Add Alert Rule",
        description: "Open the Add Alert Rule modal",
        shortcuts: ["ctrl+shift+e"],
        action: () => {
          const api = store.get(gridApiAtomFamily(store.get(focusedPaneIdAtom)));
          const range = getProcessedSelectedCellsByRange({ api });
          const cells = range.flat();
          if (cells.length !== 1) return;
          if (!cells[0].productId) return;
          alertsModal.open({
            active: "add",
            range,
            width: 700,
            height: 470,
          });
        },
      },
    ],
    [alertsModal],
  );

  const allShortcuts = useMemo(() => [...globalKeyboardShortcuts, ...contextShortcuts], [contextShortcuts]);

  const debouncedAction = useThrottledCallback(
    (shortcut: TGlobalKeyboardShortcut) => {
      shortcut.action();
    },
    [],
    debounce ?? 1000,
    true,
  );

  const listenerKeydown = useCallback(
    (event: KeyboardEvent) => {
      const qaShortcut = matchQuickAccessShortcut(quickAccess, event);

      if (qaShortcut) {
        handleQuickAccess(qaShortcut);
        return;
      }

      const shortcut = matchGlobalShortcut(event, allShortcuts);

      if (shortcut) {
        debouncedAction(shortcut);
      }
    },
    [debouncedAction, quickAccess, handleQuickAccess, allShortcuts],
  );

  useEffect(() => {
    window.addEventListener("keydown", listenerKeydown);

    return () => {
      window.removeEventListener("keydown", listenerKeydown);
    };
  }, [listenerKeydown]);
}

function matchQuickAccessShortcut(quickAccess: TQuickAccess[], event: KeyboardEvent) {
  if ((!ctrl(event) && !modifierKeyPressed(event, "alt")) || !modifierKeyPressed(event, "shift")) {
    return false;
  }

  const key = event.code.includes("Digit") ? event.code.replace("Digit", "") : event.key;

  const shortcut = quickAccess.find((qa) => qa.shortcut_number.toString() === key.toString());

  return shortcut;
}

export function matchGlobalShortcut(
  event: KeyboardEvent,
  shortcuts: TGlobalKeyboardShortcut[],
): TGlobalKeyboardShortcut | null {
  for (const shortcut of shortcuts) {
    const shortcutMatch = shortcut.shortcuts.reduce((acc, sc) => {
      if (keysMatch(event, sc)) {
        acc.push(shortcut);
      }
      return acc;
    }, [] as TGlobalKeyboardShortcut[])?.[0];

    if (shortcutMatch) {
      return shortcutMatch;
    }
  }

  return null;
}
